/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { Formik } from 'formik';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';

import { Alert, CircularProgress, Typography } from '@mui/material';

import { AlignCenter } from '../../components/AlignCenter/AlignCenter';
import { Namespaces } from '../../i18n';
import { ProjectApiService } from '../../services/ProjectApiService';
import { RootState } from '../../store';
import { useCreateEntityOverlayStyles } from '../../styles/createEntityOverlay';
import { ApiClientError } from '../../types/api/ApiClientError';
import { DataState } from '../../types/common/DataState';
import { CreateProjectRequestBody } from '../../types/project/CreateProjectRequestBody';
import { dataError, dataLoaded, dataLoading, dataNotAsked, isDataLoading, isDataNotAsked } from '../../utils/dataState';
import { getDummyArray } from '../../utils/getDummyArray';
import { reactToCreateProject } from '../../utils/reactions';
import { useApiValidation } from '../../utils/useApiValidation';
import { useObjectState } from '../../utils/useObjectState';
import { CommonCreateContainer } from '../common/CreateContainer/CommonCreateContainer';
import { FormType } from '../common/CreateContainer/types';
import { initialFormValues, validationSchema } from './CreateProjectOverlay.consts';
import { CreateProjectFormValues, OnProjectCreateCallback } from './CreateProjectOverlay.types';

const mapState = (state: RootState) => ({
  createProject: state.createProject,
  organizations: state.organizations,
});

const connector = connect(mapState);
type PropsFromRedux = ConnectedProps<typeof connector>;

type CreateProjectOverlayFormProps = PropsFromRedux & {
  onProjectCreate: OnProjectCreateCallback;
};

type State = {
  errorMessage?: string;
  dataState: DataState;
};

const CreateProjectOverlayFormComponent = memo<CreateProjectOverlayFormProps>(props => {
  const { onProjectCreate, organizations, createProject } = props;
  const classes = useCreateEntityOverlayStyles();
  const [state, setState] = useObjectState<State>({
    dataState: dataNotAsked(),
  });
  const { dataState } = state;

  const { t } = useTranslation([Namespaces.CreateProject, Namespaces.Common]);
  const { setApiError, resetApiError, hasFieldApiError, getFieldApiError } = useApiValidation();

  const initialValues = useMemo<CreateProjectFormValues>(
    () => ({
      ...initialFormValues,
      organizationId: createProject.organizationId,
      thumbnailsOptions: getDummyArray({ length: 4 }).map(seed => ({ type: 'seed', seed })),
    }),
    [createProject.organizationId],
  );

  const isLoading = useMemo(() => isDataNotAsked(organizations.dataState), [organizations.dataState]);
  const isError = false;

  const onFormCompleted = async (values: CreateProjectFormValues) => {
    setState({
      dataState: dataLoading(),
    });
    resetApiError();
    try {
      const data: CreateProjectRequestBody = {
        name: values.name.trim(),
        description: values.description?.trim() || null,
      };
      const { thumbnail } = values;
      const organizationId = values.organizationId!;
      const result = await ProjectApiService.createProject({
        organizationId,
        data,
      });
      if (thumbnail) {
        await ProjectApiService.updateProjectThumbnail({
          organizationId,
          projectId: result.data.payload.id,
          body: thumbnail.type === 'base64' ? { base64: thumbnail.base64 } : { seed: thumbnail.seed },
        });
      }
      onProjectCreate(result.data.payload, { organizationId });
      void reactToCreateProject({ organizationId });
      setState({ dataState: dataLoaded() });
    } catch (e) {
      const error = e as ApiClientError;
      if (error.response) {
        setApiError(error);
      }
      setState({ dataState: dataError() });
    }
  };

  const onSubmit = (values: CreateProjectFormValues) => {
    void onFormCompleted(values);
  };

  const isFetching = useMemo(() => isDataLoading(dataState), [dataState]);

  return (
    <div className={classes.root}>
      {isLoading ? (
        <AlignCenter>
          <CircularProgress color="primary" size={40} />
        </AlignCenter>
      ) : isError ? (
        <Alert severity="error">{t(`${Namespaces.Common}:errors.unexpected_error`)}</Alert>
      ) : (
        <>
          <Typography classes={{ h2: classes.title }} variant="h2">
            {t('labels.new_project')}
          </Typography>
          <Formik
            onSubmit={onSubmit}
            initialValues={initialValues}
            validationSchema={validationSchema}
            validateOnBlur
            validateOnChange
          >
            {formProps => (
              <CommonCreateContainer
                formProps={formProps}
                resetApiError={resetApiError}
                getFieldApiError={getFieldApiError}
                hasFieldApiError={hasFieldApiError}
                type={FormType.PROJECT}
                isFetchingOrganizations={isFetching}
                errorMessage={state.errorMessage}
              />
            )}
          </Formik>
        </>
      )}
    </div>
  );
});

export const CreateProjectOverlayForm = connector(CreateProjectOverlayFormComponent);
